import React from "react";
import type { ReactNode } from "react";
import { FormControl, Select, MenuItem, Box, Typography } from "@mui/material";
import type { SelectChangeEvent } from "@mui/material";
import { PeopleAltOutlined as CustomersIcon } from "@mui/icons-material";
import { useAMCustomers } from "../../features/account-manager/hooks/useAMCustomers";
import { useAccountManager } from "../../contexts/AccountManagerContext";
import { THEME_CONSTANTS } from "../../theme/constants";
import { topbarSelectSx } from "./topbarDropdownStyles";

const CustomerIcon: React.FC = () => (
  <Box
    sx={{
      width: 18,
      height: 18,
      borderRadius: THEME_CONSTANTS.borderRadius.round,
      bgcolor: THEME_CONSTANTS.colors.grey[200],
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <CustomersIcon
      sx={{
        fontSize: 14,
        color: THEME_CONSTANTS.colors.primary[700],
        opacity: 0.35,
      }}
    />
  </Box>
);

interface AMCustomerDropdownProps {
  /** Render in topbar style — matches the search bar dimensions and DS tokens. */
  topbar?: boolean;
}

export const AMCustomerDropdown: React.FC<AMCustomerDropdownProps> = ({
  topbar = false,
}) => {
  const { selectedCustomerId, setSelectedCustomerId } = useAccountManager();
  const { data: customers = [], isLoading } = useAMCustomers();

  const handleChange = (event: SelectChangeEvent) => {
    const value = event.target.value;
    setSelectedCustomerId(value === "all" ? null : parseInt(value, 10));
  };

  const renderValue = (selected: unknown): ReactNode => {
    if (selected === "all") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CustomerIcon />
          <Typography component="span" variant="body2">
            All Customers
          </Typography>
        </Box>
      );
    }

    const client = customers.find(
      (c) => c.clientTenantId.toString() === selected,
    );

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CustomerIcon />
        <Typography component="span" variant="body2" noWrap>
          {client?.clientTenantName ?? "All Customers"}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      sx={
        topbar
          ? { width: "100%", display: "block" }
          : { px: 2, py: 1.5, mb: 1, width: "100%", display: "block" }
      }
    >
      <FormControl fullWidth size="small">
        <Select
          value={
            selectedCustomerId === null ? "all" : selectedCustomerId.toString()
          }
          onChange={handleChange}
          displayEmpty
          disabled={isLoading}
          aria-label="Select customer"
          sx={topbar ? topbarSelectSx : undefined}
          renderValue={renderValue}
        >
          <MenuItem value="all">
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CustomerIcon />
              <Typography
                variant="body2"
                fontWeight={THEME_CONSTANTS.typography.fontWeight.medium}
              >
                All Customers
              </Typography>
            </Box>
          </MenuItem>
          {customers.map((client) => (
            <MenuItem
              key={client.clientTenantId}
              value={client.clientTenantId.toString()}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CustomerIcon />
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {client.clientTenantName}
                  </Typography>
                  {client.registrationNo && (
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {client.registrationNo}
                    </Typography>
                  )}
                </Box>
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default AMCustomerDropdown;
